import type { PromotionType } from '@weft/types';
import { type PromotionInput, PROMOTION_TYPES } from '@weft/types';
import { entries, isNotNullOrUndefined } from '@weft/utils';
import { vyFormRegister } from 'apps/app/src/utils/react-hook-forms-utils';
import cn from 'classnames';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Input } from '../../../../../components/design/Input';
import { useFormErrorNotificationContext } from '../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { PROMOTION_SETTINGS } from '../../create-plan-page-consts/promotions-settings';
import type { NewPlanForm } from '../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from '../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { PromotionTypeDropdown } from './PromotionTypeDropdown';

export type PromotionCardHeaderEditProps = {
  promotionIndex: number,
};

export const PromotionCardHeaderEdit: React.FC<PromotionCardHeaderEditProps> = ({ promotionIndex }) => {
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const { getFieldState, register, getValues } = useFormContext<PlanInputForm, NewPlanForm>();

  const nameRegister = register(`promotions.${promotionIndex}.name`);
  const allCurrentPromotions = getValues('promotions');
  const currentPromotionType = getValues(`promotions.${promotionIndex}.promotionModel.promotionType`);
  const descriptionRegister = vyFormRegister(register, `promotions.${promotionIndex}.description`);

  registerErrorField(nameRegister.name, CREATE_PLAN_TABS.promotions);
  registerErrorField(descriptionRegister.name, CREATE_PLAN_TABS.promotions);

  return (
    <div className='divide-line border-line flex flex-wrap gap-10 border-b pb-5'>
      <div className="min-w-1/4 flex flex-1">
        <Input
          label='Name'
          placeholder='Text here'
          {...nameRegister}
          defaultValue={getValues(`promotions.${promotionIndex}.name`) ?? ''}
          className={cn({ 'border border-input-error': getFieldState(nameRegister.name).invalid })}
          errorMessage={getFieldState(nameRegister.name).error?.message}
          debounce={false}
        />
      </div>

      <div className="min-w-60 flex flex-1">
        <PromotionTypeDropdown
          promotionIndex={promotionIndex}
          promotionOptions={getPromotionOptions(currentPromotionType, allCurrentPromotions ?? [])}
        />
      </div>

      <div className="min-w-1/4 flex flex-1">
        <Input
          label='Description'
          placeholder='Add description here'
          {...descriptionRegister}
          defaultValue={getValues(`promotions.${promotionIndex}.description`) ?? ''}
          className={cn({ 'border border-input-error': getFieldState(descriptionRegister.name).invalid })}
          errorMessage={getFieldState(descriptionRegister.name).error?.message}
          debounce={false}
        />
      </div>
    </div>
  );
};

const getPromotionOptions = (currentPromotionType: PromotionType, allCurrentPromotions: PromotionInput[]) => {
  const promotionOptions: Record<string, [string, boolean]> = Object.fromEntries(entries(PROMOTION_SETTINGS).map(([promotionType, { label, disabled = false }]) => {
    if (promotionType === PROMOTION_TYPES.Percentage && isPercentagePromotionDisabled(allCurrentPromotions, currentPromotionType)) {
      return [promotionType, [label, true]];
    }

    return [promotionType, [label, disabled]]; 
  }));

  return promotionOptions;
};

const isPercentagePromotionDisabled = (promotions: PromotionInput[], currentChosenPromotionType: PromotionType) => {
  return isPercentagePromotionExists(promotions) && currentChosenPromotionType !== PROMOTION_TYPES.Percentage;
};

const isPercentagePromotionExists = (promotions: PromotionInput[]) => {
  const foundPromotion = promotions.find(promotion => promotion.promotionModel.promotionType === PROMOTION_TYPES.Percentage);

  return isNotNullOrUndefined(foundPromotion);
};
